import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Text from "../../../components/atoms/Text";
import { DateRangePicker } from "../../../components/molecules/DateRangePicker";
import { getDateStringFromToday } from "../../../utils/TimeUtil";
import { AdminDrugIdentificationTable, SearchCondition } from "../../../components/templates/AdminDrugIdentificationTemplate/AdminDrugIdentificationTable";
import {
  fetchDrugIdentificationList,
  deleteDrugIdentification,
  DrugIdentificationListParams,
  DrugIdentificationWithUser
} from "../../../api/drugIdentificationService";
import Button from "../../../components/atoms/Button";
import Switch from "../../../components/atoms/Switch";
import { ToastManager } from "../../../components/organisms/CommonUI/ToastPopup/ToastManager";
import { WaitingManager } from "../../../components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ConfirmManager } from "../../../components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { SlideManager } from "../../../components/organisms/CommonUI/SlidePopup/SlideManager";
import DrugSearchPanel from "../../../components/organisms/DrugSearchPanel";

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const FilterSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  width: 100%;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled(Text)`
  min-width: 80px;
  font-weight: 500;
  color: #666;
`;

const DateFilterWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
`;

const QuickDateButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#4A90E2" : "#ddd")};
  background: ${(props) => (props.active ? "#4A90E2" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#666")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    border-color: #4a90e2;
    color: ${(props) => (props.active ? "#fff" : "#4A90E2")};
  }
`;

const AdminDrugIdentificationsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [identifications, setIdentifications] = useState<DrugIdentificationWithUser[]>([]);

  // 필터 상태
  const [startDate, setStartDate] = useState(getDateStringFromToday({ days: -7 }));
  const [endDate, setEndDate] = useState(getDateStringFromToday());
  const [quickDateRange, setQuickDateRange] = useState("1week");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  // 날짜 빠른 선택
  const handleQuickDateSelect = (range: string) => {
    setQuickDateRange(range);
    const today = getDateStringFromToday();

    switch (range) {
      case "today":
        setStartDate(today);
        setEndDate(today);
        break;
      case "1week":
        setStartDate(getDateStringFromToday({ days: -7 }));
        setEndDate(today);
        break;
      case "1month":
        setStartDate(getDateStringFromToday({ days: -30 }));
        setEndDate(today);
        break;
      case "3months":
        setStartDate(getDateStringFromToday({ days: -90 }));
        setEndDate(today);
        break;
    }
  };

  // 날짜 변경 핸들러
  const handleDateChange = (name: "start" | "end", date: Date) => {
    const dateString = date.toISOString().split("T")[0];
    if (name === "start") {
      setStartDate(dateString);
    } else {
      setEndDate(dateString);
    }
    setQuickDateRange("");
  };

  // 데이터 로드
  const loadIdentifications = async () => {
    try {
      setLoading(true);
      WaitingManager.show({ message: "의약품 식별 이력을 불러오는 중..." });

      const params: DrugIdentificationListParams = {
        startDate,
        endDate: endDate + " 23:59:59",
        withUser: true,
        withResults: true,
        includeDeleted,
        limit: 1000,
        offset: 0,
      };

      const response = await fetchDrugIdentificationList(params);

      setIdentifications(response.data);
    } catch (error) {
      console.error("Failed to load drug identifications:", error);
      ToastManager.show({ content: "의약품 식별 이력을 불러오는데 실패했습니다." });
    } finally {
      setLoading(false);
      WaitingManager.hide();
    }
  };

  // 페이지별 데이터
  const paginatedIdentifications = (identifications || []).slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // 상세보기 팝업
  const handleViewDetail = (id: number) => {
    // 화면 크기의 90%를 차지하도록 설정, 최대 1920x1080
    const width = Math.min(1920, window.screen.width * 0.9);
    const height = Math.min(1080, window.screen.height * 0.9);
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      `/admin/drug-identifications/${id}/popup`,
      `drug-identification-${id}`,
      `width=${width},height=${height},left=${left},top=${top},` +
        `toolbar=no,location=no,directories=no,status=no,menubar=no,` +
        `scrollbars=yes,resizable=yes`
    );
  };

  // 삭제 처리
  const handleDelete = async (item: DrugIdentificationWithUser) => {
    const userEmail = item.user?.email || "사용자";

    return new Promise<void>((resolve) => {
      ConfirmManager.show({
        title: "의약품 식별 이력 삭제",
        body: `${userEmail}님의 의약품 식별 이력 #${item.id}을(를) 삭제하시겠습니까?`,
        buttons: [
          <Button
            key="cancel"
            size="Medium"
            type="Text"
            variant="Outlined"
            color="Secondary"
            onClick={() => {
              ConfirmManager.hide();
              resolve();
            }}
          >
            취소
          </Button>,
          <Button
            key="confirm"
            size="Medium"
            type="Text"
            variant="Contained"
            color="Negative"
            onClick={async () => {
              ConfirmManager.hide();
              try {
                WaitingManager.show({ message: "삭제 처리 중..." });

                const result = await deleteDrugIdentification(item.id!);

                if (result.error === 0) {
                  ToastManager.show({ content: "의약품 식별 이력이 삭제되었습니다." });
                  loadIdentifications(); // 목록 새로고침
                } else {
                  ToastManager.show({ content: "삭제에 실패했습니다." });
                }
              } catch (error) {
                console.error("Delete failed:", error);
                ToastManager.show({ content: "삭제 처리에 실패했습니다." });
              } finally {
                WaitingManager.hide();
              }
              resolve();
            }}
          >
            삭제
          </Button>,
        ],
      });
    });
  };

  // 검색 조건 더블클릭 핸들러 - 슬라이드 패널 열기
  const handleConditionDoubleClick = (condition: SearchCondition) => {
    SlideManager.show({
      body: <DrugSearchPanel initialCondition={condition} />,
      width: "40%",
      minWidth: "560px",
      direction: "right",
      needCloseButton: true,
      noBackdrop: true,
    });
  };

  // 초기 로드
  useEffect(() => {
    loadIdentifications();
  }, []);

  return (
    <Container>
      <Header>
        <Title>의약품 식별 이력</Title>
      </Header>

      <FilterSection>
        <FilterRow>
          <FilterLabel>기간 선택</FilterLabel>
          <DateFilterWrapper>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChangeDate={handleDateChange}
              disabled={loading}
            />
            <QuickDateButton
              active={quickDateRange === "today"}
              onClick={() => handleQuickDateSelect("today")}
            >
              오늘
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "1week"}
              onClick={() => handleQuickDateSelect("1week")}
            >
              1주일
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "1month"}
              onClick={() => handleQuickDateSelect("1month")}
            >
              1개월
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "3months"}
              onClick={() => handleQuickDateSelect("3months")}
            >
              3개월
            </QuickDateButton>
            <Button
              size="Medium"
              type="Text"
              variant="Contained"
              color="Primary"
              onClick={loadIdentifications}
              disabled={loading}
            >
              조회
            </Button>
          </DateFilterWrapper>
        </FilterRow>

        <FilterRow>
          <FilterLabel>옵션</FilterLabel>
          <Switch
            size="Medium"
            isActive={includeDeleted}
            label="삭제된 항목 포함"
            labelPosition="Right"
            onClick={() => setIncludeDeleted(!includeDeleted)}
          />
        </FilterRow>
      </FilterSection>

      <AdminDrugIdentificationTable
        identifications={paginatedIdentifications}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        total={(identifications || []).length}
        onPageChange={setCurrentPage}
        onViewDetail={handleViewDetail}
        onDelete={handleDelete}
        onConditionDoubleClick={handleConditionDoubleClick}
      />
    </Container>
  );
};

export default AdminDrugIdentificationsPage;
